import axios, { AxiosInstance } from 'axios';
import { resolveApiError } from './error-message';
import { TenantSessionChangedError, requireCurrentTenantSession } from '../utils/tenant-session';

// 直引 import.meta.env（vite 构建期静态替换）；`(import.meta as any).env?.` 写法会让
// define 替换失效、恒落 '/api' 兜底，将来用 env 切后端域名时不生效
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

type SessionConfig = { tenantSessionJwt?: string | null };

export function createApi(getJwt: () => string | null, onUnauthorized: (requestJwt: string | null) => void): AxiosInstance {
  const instance = axios.create({ baseURL: API_BASE });

  instance.interceptors.request.use((cfg) => {
    const jwt = getJwt();
    requireCurrentTenantSession(jwt, getJwt());
    (cfg as typeof cfg & SessionConfig).tenantSessionJwt = jwt;
    if (jwt) cfg.headers.Authorization = `Bearer ${jwt}`;
    return cfg;
  });

  instance.interceptors.response.use(
    (res) => {
      const snapshot = (res.config as typeof res.config & SessionConfig).tenantSessionJwt;
      requireCurrentTenantSession(snapshot ?? null, getJwt());
      return res;
    },
    (err) => {
      if (err instanceof TenantSessionChangedError) return Promise.reject(err);
      if (err.config) {
        const snapshot = (err.config as typeof err.config & SessionConfig).tenantSessionJwt;
        requireCurrentTenantSession(snapshot ?? null, getJwt());
        if (err.response?.status === 401) onUnauthorized(snapshot ?? null);
      }
      // 统一把报错文案归一为中文（先解析，再改写，避免覆盖后丢失原始信息）。
      // 原始机器码保留到 data.error_code，需要按码分支的页面用 rawErrorCode() 读。
      const friendly = resolveApiError(err);
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        if (data.error_code === undefined) data.error_code = data.error;
        data.error = friendly;
        data.message = friendly;
      }
      err.friendlyMessage = friendly;
      err.message = friendly; // 覆盖 axios 默认英文 message（用于 e.message 展示）
      return Promise.reject(err);
    },
  );

  return instance;
}
